import React, { useEffect, useRef, useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { Avatar } from './Avatar';
import type { PhotoUploadMessage as PhotoUploadMessageType } from '@/types';

interface PhotoUploadMessageProps {
    message: PhotoUploadMessageType;
    botAvatar: string;
    customStyles?: {
        primaryColor?: string;
        secondaryColor?: string;
        borderRadius?: number;
        fontSize?: number;
    };
    /** Resolves to an error message to show, or null when the photo was accepted. */
    onSubmit?: (file: File) => Promise<string | null>;
    disabled?: boolean;
}

export const PhotoUploadMessage: React.FC<PhotoUploadMessageProps> = ({
    message,
    botAvatar,
    customStyles,
    onSubmit,
    disabled = false,
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const primaryColor = customStyles?.primaryColor || '#a855f7';
    const secondaryColor = customStyles?.secondaryColor || '#764ba2';
    const borderRadius = customStyles?.borderRadius || 16;
    const fontSize = customStyles?.fontSize ?? 14;
    const locked = disabled || uploading || !onSubmit;

    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const chosen = e.target.files?.[0];
        e.target.value = '';
        if (!chosen) return;
        if (!chosen.type.startsWith('image/')) {
            setError('Please choose a photo');
            return;
        }
        setError(null);
        setFile(chosen);
        setPreviewUrl(URL.createObjectURL(chosen));
    };

    const handleContinue = async () => {
        if (!file || locked) return;
        setUploading(true);
        setError(null);
        const err = await onSubmit!(file);
        setUploading(false);
        if (err) setError(err);
    };

    return (
        <div className="zentri-flex zentri-items-start zentri-gap-3 zentri-mb-4 zentri-animate-fade-in">
            <Avatar src={botAvatar} alt="Bot" size="md" />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 280 }}>
                <button
                    type="button"
                    onClick={() => !locked && inputRef.current?.click()}
                    disabled={locked}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        width: '100%',
                        margin: 0,
                        padding: '22px 18px 18px',
                        background: '#ffffff',
                        border: `2px dashed ${primaryColor}`,
                        borderRadius: `${borderRadius}px`,
                        cursor: locked ? 'default' : 'pointer',
                        opacity: disabled && !previewUrl ? 0.6 : 1,
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
                    }}
                >
                    <p style={{
                        margin: 0,
                        fontWeight: 700,
                        color: '#111827',
                        lineHeight: 1.35,
                        fontSize: `${fontSize + 1}px`,
                    }}>
                        {message.text}
                    </p>

                    {previewUrl ? (
                        <img
                            src={previewUrl}
                            alt="Your photo"
                            style={{
                                width: '100%',
                                maxHeight: 160,
                                objectFit: 'cover',
                                marginTop: 14,
                                borderRadius: `${Math.max(borderRadius - 6, 8)}px`,
                            }}
                        />
                    ) : (
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 64,
                            height: 64,
                            marginTop: 16,
                            borderRadius: 999,
                            background: `${primaryColor}18`,
                        }}>
                            <Camera style={{ width: 30, height: 30, color: primaryColor }} />
                        </span>
                    )}

                    {message.hint && (
                        <p style={{
                            margin: '14px 0 0',
                            color: '#6b7280',
                            lineHeight: 1.4,
                            fontSize: `${Math.max(fontSize - 1, 12)}px`,
                        }}>
                            {message.hint}
                        </p>
                    )}
                    {previewUrl && !disabled && !uploading && (
                        <p style={{
                            margin: '8px 0 0',
                            textDecoration: 'underline',
                            fontSize: `${Math.max(fontSize - 2, 11)}px`,
                            color: primaryColor,
                        }}>
                            Choose a different photo
                        </p>
                    )}
                </button>

                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="zentri-hidden"
                    style={{ display: 'none' }}
                />

                {error && (
                    <p className="zentri-text-red-600" style={{ fontSize: `${fontSize - 1}px` }}>{error}</p>
                )}

                {!disabled && (
                    <button
                        type="button"
                        onClick={handleContinue}
                        disabled={!file || locked}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            width: '100%',
                            margin: 0,
                            padding: '12px 16px',
                            border: 'none',
                            color: '#ffffff',
                            fontWeight: 600,
                            letterSpacing: '0.01em',
                            background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
                            borderRadius: `${borderRadius}px`,
                            boxShadow: !file || locked ? 'none' : '0 8px 18px rgba(0, 0, 0, 0.12)',
                            opacity: !file || locked ? 0.45 : 1,
                            cursor: !file || locked ? 'not-allowed' : 'pointer',
                            fontSize: `${fontSize + 1}px`,
                        }}
                    >
                        {uploading && <Loader2 className="zentri-w-4 zentri-h-4 zentri-animate-spin" />}
                        {uploading ? 'Uploading...' : (message.buttonLabel || 'Continue')}
                    </button>
                )}
            </div>
        </div>
    );
};
